"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { SectionHeader } from "@/components/ui";
import { DATAIMPULSE_TARGETING_DOCS_URL, PRICING_VERIFIED_DATE } from "./pricing-data";

const MotionDiv = motion.div;

interface ExampleRow {
  targeting: string;
  proxidize: string;
  dataimpulse: string;
}

// 100 GB/month mobile at the standard ($2/GB) rate both providers publish below 1,000 GB. City or
// ASN selection doubles DataImpulse's rate per its targeting docs; Proxidize's rate is unaffected
// since city and ASN targeting are already included.
const EXAMPLE_ROWS: ExampleRow[] = [
  { targeting: "Country only", proxidize: "$200", dataimpulse: "$200" },
  { targeting: "City or ASN selection on all traffic", proxidize: "$200", dataimpulse: "$400" },
];

export default function DataimpulseCostExample() {
  const prefersReducedMotion = useReducedMotion();

  const fadeUp = (delay = 0) => ({
    initial: prefersReducedMotion ? {} : { opacity: 0, y: 24 },
    whileInView: prefersReducedMotion ? {} : { opacity: 1, y: 0 },
    viewport: scrollRevealViewport,
    transition: { duration: 0.55, delay },
  });

  return (
    <section className="py-section border-border border-b" aria-labelledby="cost-example-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv {...fadeUp(0)} className="mb-10 max-w-2xl">
          <SectionHeader
            eyebrow="Worked Example"
            id="cost-example-heading"
            heading="100 GB/month of mobile traffic, at the standard rate"
            body="A fixed volume makes the targeting cost concrete instead of abstract. Both providers charge $2/GB standard below 1,000 GB/month — the difference shows up only once targeting gets specific."
          />
        </MotionDiv>

        <MotionDiv
          {...fadeUp(0.08)}
          className="border-border overflow-x-auto rounded-xl border md:overflow-x-visible"
        >
          <table className="w-full min-w-[480px] border-collapse text-sm">
            <caption className="sr-only">
              Monthly cost for 100 GB of mobile traffic, Proxidize vs DataImpulse, by targeting
              granularity
            </caption>
            <thead>
              <tr className="border-border bg-card border-b">
                <th scope="col" className="text-muted-foreground px-5 py-4 text-left font-medium">
                  Targeting
                </th>
                <th scope="col" className="text-foreground px-5 py-4 text-left font-semibold">
                  Proxidize
                </th>
                <th scope="col" className="text-foreground px-5 py-4 text-left font-semibold">
                  DataImpulse
                </th>
              </tr>
            </thead>
            <tbody>
              {EXAMPLE_ROWS.map((row) => (
                <tr key={row.targeting} className="border-border border-b last:border-b-0">
                  <th scope="row" className="text-foreground px-5 py-4 text-left font-medium">
                    {row.targeting}
                  </th>
                  <td className="text-muted-foreground px-5 py-4">{row.proxidize}</td>
                  <td className="text-muted-foreground px-5 py-4">{row.dataimpulse}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </MotionDiv>

        <MotionDiv {...fadeUp(0.16)} className="mt-6 max-w-3xl">
          <p className="text-muted-foreground text-xs leading-relaxed">
            Calculated examples using each provider&rsquo;s published standard rates for 100 GB of
            mobile traffic, excluding discounts or custom agreements. City or ASN selection is
            billed at{" "}
            <a
              href={DATAIMPULSE_TARGETING_DOCS_URL}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="hover:text-foreground underline underline-offset-2 transition-colors"
            >
              double DataImpulse&rsquo;s standard rate
            </a>
            ; Proxidize includes it in the $2/GB rate shown for both rows. Proxidize bills this
            volume as a monthly plan; DataImpulse bills it as non-expiring pay-as-you-go traffic —
            confirm both providers cover the locations you need before comparing further. Verified{" "}
            {PRICING_VERIFIED_DATE}.
          </p>
        </MotionDiv>
      </div>
    </section>
  );
}
